import { Box, Tooltip, Typography } from '@mui/material';
import React from 'react';

import {
  GIFT_BUDGET_INLINE_SEPARATOR,
  GIFT_BUDGET_NOT_USED_INLINE_LABEL,
  GIFT_BUDGET_NOT_USED_LABEL,
  GIFT_BUDGET_REMAINING_SUFFIX,
  GIFT_BUDGET_SPENT_SUFFIX,
} from '../../../constants/reports';
import { GiftBudgetUsageKindEnum, IReportGiftBudget, IReportGiftBudgetUsage } from '../../../types/reports';
import { formatMarketplaceSpent } from '../../../utils/reports';
import Price from '../../Price/Price';

interface IProps {
  budget: IReportGiftBudget | null;
  usage?: IReportGiftBudgetUsage | null;
  inline?: boolean;
}

const ReportGiftBudget = ({ budget, usage, inline }: IProps) => {
  if (!budget) {
    return <span>—</span>;
  }

  const total = <Price value={{ price: budget.value, currency: budget.currency }} fixedDecimalScale={false} />;

  if (!usage || usage.kind === GiftBudgetUsageKindEnum.Pending) {
    return total;
  }

  const isSpent = usage.kind === GiftBudgetUsageKindEnum.Spent;
  const spentLabel = `${formatMarketplaceSpent(usage.spent, budget.currency)} ${GIFT_BUDGET_SPENT_SUFFIX}`;
  const remainingLabel = `${formatMarketplaceSpent(usage.remaining, budget.currency)} ${GIFT_BUDGET_REMAINING_SUFFIX}`;

  if (inline) {
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        {total}
        <Typography component="span">{GIFT_BUDGET_INLINE_SEPARATOR}</Typography>
        {isSpent ? (
          <Tooltip title={remainingLabel} arrow placement="top">
            <Typography component="span">{spentLabel}</Typography>
          </Tooltip>
        ) : (
          <Typography component="span">{GIFT_BUDGET_NOT_USED_INLINE_LABEL}</Typography>
        )}
      </Box>
    );
  }

  const detail = isSpent ? (
    <Tooltip title={remainingLabel} arrow placement="top">
      <Typography component="span" variant="body2" color="text.secondary" sx={{ alignSelf: 'flex-start' }}>
        {spentLabel}
      </Typography>
    </Tooltip>
  ) : (
    <Typography component="span" variant="body2" color="text.disabled">
      {GIFT_BUDGET_NOT_USED_LABEL}
    </Typography>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {total}
      {detail}
    </Box>
  );
};

export default ReportGiftBudget;
